import React, { useState, useLayoutEffect, useRef } from 'react';
import { Field } from 'react-final-form';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { Editor } from 'react-draft-wysiwyg';
import htmlToDraft from 'html-to-draftjs';
import { useMediaLayout } from 'use-media';

import { FieldLabel, ProtipTooltip, FieldCopy, ProtipLink } from '..';
import FieldWysiwygErrorBoundary from './FieldWysiwygErrorBoundary';
import css from './FieldWysiwyg.module.css';

const createEditorStateFromHtml = html => {
  const safeHtml = html || '';

  try {
    const blocksFromHtml = htmlToDraft(safeHtml);

    if (!blocksFromHtml?.contentBlocks?.length) {
      return EditorState.createEmpty();
    }

    const { contentBlocks, entityMap } = blocksFromHtml;
    const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);

    return EditorState.createWithContent(contentState);
  } catch {
    return EditorState.createEmpty();
  }
};

const FieldWysiwygComponent = ({
  id,
  label,
  labelDescription,
  input,
  protip,
  placeholder,
  help,
}) => {
  const [wysiwygState, setWysiwygState] = useState(() => createEditorStateFromHtml(input.value));
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isReady, setIsReady] = useState(!protip);

  const contentRef = useRef(wysiwygState.getCurrentContent());

  useLayoutEffect(() => {
    if (protip) {
      setIsReady(true);
    }
  }, [protip]);

  // We use this hook to control different tooltip behaviour on mobile an desktop
  const isWide = useMediaLayout({ minWidth: '1024px' });

  if (protip && !isReady) {
    return null;
  }

  const fieldHelp = help ? <FieldCopy>{help}</FieldCopy> : null;

  const handleOnBlur = () => {
    setIsTooltipVisible(false);
  };

  const handleOnFocus = () => {
    if (isWide) {
      setIsTooltipVisible(true);
    }
  };

  const handleTooltipVisibility = isVisible => {
    setIsTooltipVisible(isVisible);
  };

  const handleEditorStateChange = editorState => {
    const newContent = editorState.getCurrentContent();

    setWysiwygState(editorState);

    // Avoid parent form updates for selection/focus-only changes. Draft.js is
    // sensitive to unnecessary re-renders around contentEditable updates.
    if (contentRef.current !== newContent) {
      contentRef.current = newContent;

      const html = newContent.hasText() ? draftToHtml(convertToRaw(newContent)) : '';

      input.onChange(html);
    }
  };

  const renderEditor = () => {
    return (
      <div className="notranslate" translate="no">
        <Editor
          stripPastedStyles
          spellCheck={false}
          placeholder={placeholder}
          wrapperId={id}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
          editorState={wysiwygState}
          wrapperClassName="package-description-wrapper"
          editorClassName={css.editor}
          onEditorStateChange={handleEditorStateChange}
          toolbar={{
            options: ['inline', 'link', 'list'],
            inline: {
              inDropdown: false,
              className: undefined,
              component: undefined,
              dropdownClassName: undefined,
              options: ['bold', 'italic', 'strikethrough'],
            },
            link: {
              popupClassName: css.linkPopup,
              options: ['link', 'unlink'],
            },
            list: {
              options: ['ordered', 'unordered'],
            },
          }}
        />
      </div>
    );
  };

  return (
    <div>
      {label ? (
        <FieldLabel htmlFor={id} description={labelDescription}>
          {label}
        </FieldLabel>
      ) : null}

      <FieldWysiwygErrorBoundary>
        {protip ? (
          <ProtipTooltip
            placement={isWide ? 'right' : 'bottom'}
            body={protip}
            tooltipShown={isTooltipVisible}
            onTooltipShown={handleTooltipVisibility}
            className={css.proTipTextArea}
            trigger="none"
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: ({ placement }) => {
                    if (placement === 'bottom') {
                      return [0, 8];
                    }

                    return [0, 12];
                  },
                },
              },
            ]}
          >
            {renderEditor()}
          </ProtipTooltip>
        ) : (
          renderEditor()
        )}
      </FieldWysiwygErrorBoundary>

      <div className={css.helpWrapper}>
        {protip && (
          <div className={css.proTipWrapper}>
            <ProtipLink
              onClick={() => {
                setIsTooltipVisible(!isTooltipVisible);
              }}
            />
          </div>
        )}

        {fieldHelp && <div>{fieldHelp}</div>}
      </div>
    </div>
  );
};

const FieldWysiwyg = props => {
  return <Field component={FieldWysiwygComponent} {...props} />;
};

export default FieldWysiwyg;
